import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, ShoppingBag, Zap, Truck, RotateCcw, Shield, ChevronDown, PackageCheck, PackageX, AlertTriangle, Ruler, X, ChevronLeft, ChevronRight, Maximize2, Star, Camera } from "lucide-react";
import { fetchProduct, fetchProducts, formatINR, fetchProductReviews, createProductReview, uploadReviewMedia, optImg } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { PaisleyDivider, Lotus, Paisley } from "@/lib/motifs";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

const COLOR_HEX = {
    maroon: "#7B1E2B", red: "#C0392B", white: "#FFFFFF", green: "#1F5D45",
    antique: "#B08D57", bronze: "#8C6239", silver: "#C0C0C0", ivory: "#FFFFF0",
    gold: "#C9A227", black: "#1A1A1A", blue: "#2E5090", pink: "#E8A0BF",
    yellow: "#E8CE68", orange: "#D2691E", purple: "#6B3FA0", teal: "#1F5D6B",
    copper: "#B4531A", beige: "#D8C3A5", navy: "#1B2A4A", peach: "#F5B895",
    mint: "#A8D5BA", coral: "#FF6F61",
};

const swatchStyle = (name) => {
    const key = (name || "").trim().toLowerCase();
    if (key === "multi") {
        return { background: "conic-gradient(from 0deg, #7B1E2B, #C9A227, #1F5D45, #2E5090, #C0392B, #7B1E2B)" };
    }
    return { backgroundColor: COLOR_HEX[key] || "#D8D8D8" };
};

// Cloudinary images ko chhota + optimized format me load karta hai (scroll smooth rehta hai)
const cld = (url, w) => (typeof url === "string" && url.includes("res.cloudinary.com") && url.includes("/image/upload/") && !url.includes("/upload/f_auto"))
    ? url.replace("/image/upload/", `/image/upload/f_auto,q_auto,w_${w}/`)
    : url;

// Video URL pehchaanne ke liye (photo aur video ek hi gallery me hote hain)
const isVideoUrl = (u) => typeof u === "string" && (/\/video\/upload\//.test(u) || /\.(mp4|mov|webm|m4v)(\?.*)?$/i.test(u));
// Video ka pehla frame (thumbnail ke liye)
const videoPoster = (u) => u.includes("/video/upload/")
    ? u.replace("/video/upload/", "/video/upload/so_0,w_200,h_250,c_fill/").replace(/\.(mp4|mov|webm|m4v)(\?.*)?$/i, ".jpg")
    : u;

const ProductDetail = () => {
    const { id } = useParams();
    const nav = useNavigate();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [size, setSize] = useState("");
    const [color, setColor] = useState("");
    const [openAccordion, setOpenAccordion] = useState("story");
    const [imgIndex, setImgIndex] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const dragRef = useRef(null);
    const { addToCart, startBuyNow, toggleWishlist, isWishlisted } = useShop();
    const { customer, authHeader } = useCustomerAuth();

    const [reviews, setReviews] = useState([]);
    const [reviewRating, setReviewRating] = useState(0);
    const [reviewHoverRating, setReviewHoverRating] = useState(0);
    const [reviewComment, setReviewComment] = useState("");
    const [reviewImages, setReviewImages] = useState([]);
    const [reviewUploading, setReviewUploading] = useState(false);
    const [reviewSubmitting, setReviewSubmitting] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
        fetchProduct(id).then((p) => {
            setProduct(p);
            setImgIndex(0);
            setSize(p.sizes?.[0] || "");
            setColor(p.colors?.[0] || "");
            fetchProducts({ occasion: p.occasion }).then((all) => setRelated(all.filter((x) => x.id !== p.id).slice(0, 4)));
        }).catch(() => nav("/shop"));
        fetchProductReviews(id).then(setReviews).catch(() => setReviews([]));
    }, [id, nav]);

    useEffect(() => {
        if (!lightboxOpen) return;
        document.body.style.overflow = "hidden";
        const onKey = (e) => { if (e.key === "Escape") setLightboxOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [lightboxOpen]);

    if (!product) return <div className="container-x py-24 text-center font-body text-[color:var(--ink-muted)]">Loading…</div>;

    // Colour ke hisaab se naam aur description
    const colorInfo = (() => {
        const d = product.color_details || {};
        const key = Object.keys(d).find((k) => k.trim().toLowerCase() === (color || "").trim().toLowerCase());
        return key ? d[key] : {};
    })();
    const shownName = (colorInfo.name || "").trim() || product.name;
    const shownStory = (colorInfo.description || "").trim() || product.story;

    const wishlisted = isWishlisted(product.id);

    // All photos for the selected colour — falls back to the default set when
    // this colour has no photos of its own
    const colorGallery = product.color_images?.[color];
    const gallery = (colorGallery?.length > 0 ? colorGallery : [product.image, ...(product.images || [])])
        .filter(Boolean)
        .filter((src, i, arr) => arr.indexOf(src) === i);

    if (product.video && !gallery.includes(product.video)) gallery.push(product.video);   // purani alag video bhi gallery me
    const slideCount = gallery.length;
    const cartProduct = { ...product, name: shownName, image: gallery.find((g) => !isVideoUrl(g)) || product.image };
    const videoActive = isVideoUrl(gallery[imgIndex]);
    const showImage = (i) => setImgIndex((i + slideCount) % slideCount);
    const showPhoto = (i) => setImgIndex((i + gallery.length) % gallery.length);

    const onDragStart = (e) => { dragRef.current = { x: e.clientX, y: e.clientY }; };
    const onDragEnd = (e) => {
        const start = dragRef.current;
        dragRef.current = null;
        if (!start) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        const delta = Math.abs(dy) > Math.abs(dx) ? dy : dx;
        if (Math.abs(delta) < 40) { if (!videoActive) setLightboxOpen(true); return; }   // a tap, not a swipe — open full screen
        if (slideCount < 2) return;
        showImage(delta < 0 ? imgIndex + 1 : imgIndex - 1);
    };
    // stock === null / undefined  →  not tracked, always available
    const tracked = product.stock !== null && product.stock !== undefined;
    const outOfStock = tracked && product.stock <= 0;
    const lowStock = tracked && product.stock > 0 && product.stock <= 10;

    const handleAdd = () => {
        if (outOfStock) { toast.error("This piece is out of stock"); return; }
        if (!size) { toast.error("Please select a size"); return; }
        addToCart(cartProduct, size);
        toast.success(`${shownName} added to cart`);
    };

    const handleBuyNow = () => {
        if (outOfStock) { toast.error("This piece is out of stock"); return; }
        if (!size) { toast.error("Please select a size"); return; }
        startBuyNow(cartProduct, size);
        nav("/checkout?buynow=1");
    };

    const handleReviewImageSelect = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        if (reviewImages.length + files.length > 3) {
            toast.error("Up to 3 photos allowed");
            return;
        }
        setReviewUploading(true);
        try {
            const uploaded = [];
            for (const file of files) {
                const { url } = await uploadReviewMedia(file, authHeader());
                uploaded.push(url);
            }
            setReviewImages((prev) => [...prev, ...uploaded]);
        } catch {
            toast.error("Photo upload failed");
        } finally {
            setReviewUploading(false);
            e.target.value = "";
        }
    };

    const handleSubmitReview = async () => {
        if (!reviewRating) { toast.error("Please pick a star rating"); return; }
        setReviewSubmitting(true);
        try {
            const created = await createProductReview(product.id, {
                rating: reviewRating,
                comment: reviewComment.trim(),
                images: reviewImages,
            }, authHeader());
            setReviews((prev) => [created, ...prev]);
            setProduct((p) => ({
                ...p,
                review_count: (p.review_count || 0) + 1,
                avg_rating: Math.round((((p.avg_rating || 0) * (p.review_count || 0)) + reviewRating) / ((p.review_count || 0) + 1) * 10) / 10,
            }));
            setReviewRating(0);
            setReviewComment("");
            setReviewImages([]);
            toast.success("Thank you for rating your Kanganya piece 🌸");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit your review");
        } finally {
            setReviewSubmitting(false);
        }
    };

    const accordions = [
        { key: "story", label: "Story of the Craft", content: shownStory },
        { key: "material", label: "Material & Care", content: `Crafted in ${product.material}. Wipe gently with a soft dry cloth. Store in a velvet pouch away from perfume and moisture.` },
        { key: "shipping", label: "Shipping & Returns", content: "Complimentary shipping across India. Dispatched in a heritage jewellery box within 2 business days. 24 Hours return for unopened pieces." },
    ];

    return (
        <div data-testid="product-detail-page">
            <div className="container-x py-12">
                <p className="font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--ink-muted)] mb-8">
                    <Link to="/" className="hover:text-[color:var(--maroon)]">Home</Link> · <Link to="/shop" className="hover:text-[color:var(--maroon)]">Shop</Link> · <span className="text-[color:var(--maroon)]">{product.name}</span>
                </p>

                <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
                    {/* Image */}
                    <div className="self-start w-full md:max-w-[440px]">
                        <div
                            className="relative border border-[color:var(--border-gold)] overflow-hidden group shine-hover bg-white select-none"
                            style={{ borderTopLeftRadius: '999px', borderTopRightRadius: '999px', touchAction: 'pan-y', cursor: gallery.length > 1 ? 'grab' : 'default' }}
                            data-testid="product-image"
                            onPointerDown={onDragStart}
                            onPointerUp={onDragEnd}
                            onPointerCancel={() => { dragRef.current = null; }}
                        >
                            {gallery.map((src, i) => isVideoUrl(src) ? null : (
                                <img
                                    key={src + i}
                                    src={cld(src, 900)}
                                    decoding="async"
                                    fetchpriority={i === 0 ? "high" : "low"}
                                    alt={`${product.name} — view ${i + 1}`}
                                    draggable={false}
                                    className={`w-full aspect-[4/5] object-cover transition-opacity duration-500 ${i === imgIndex ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}
                                />
                            ))}
                            {videoActive && (
                                <video
                                    key={gallery[imgIndex]}
                                    src={optImg(gallery[imgIndex], 1600)}
                                    autoPlay
                                    muted
                                    disablePictureInPicture
                                    onContextMenu={(e) => e.preventDefault()}
                                    loop
                                    playsInline
                                    preload="metadata"
                                    className="w-full aspect-[4/5] object-cover bg-black"
                                    data-testid="product-video"
                                />
                            )}
                            {product.tag && (
                                <span className="absolute top-6 left-6 bg-[color:var(--gold)] text-[color:var(--maroon-dark)] text-[10px] font-ui font-semibold tracking-widest-x uppercase px-3 py-1.5">
                                    {product.tag}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
                                aria-label="View full screen"
                                style={{ display: videoActive ? "none" : undefined }}
                                className="absolute top-5 right-5 z-10 w-9 h-9 flex items-center justify-center bg-[color:var(--maroon)]/70 text-[color:var(--ivory)] hover:bg-[color:var(--maroon)] transition-colors duration-300"
                            >
                                <Maximize2 size={16} strokeWidth={1.5} />
                            </button>
                            {slideCount > 1 && (
                                <>
                                    <button
                                        type="button"
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onPointerUp={(e) => e.stopPropagation()}
                                        onClick={(e) => { e.stopPropagation(); showImage(imgIndex - 1); }}
                                        aria-label="Previous"
                                        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 focus:opacity-100 w-9 h-9 flex items-center justify-center bg-[color:var(--maroon)]/70 text-[color:var(--ivory)] hover:bg-[color:var(--maroon)] transition-colors duration-300"
                                        data-testid="product-prev"
                                    >
                                        <ChevronLeft size={20} strokeWidth={1.5} />
                                    </button>
                                    <button
                                        type="button"
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onPointerUp={(e) => e.stopPropagation()}
                                        onClick={(e) => { e.stopPropagation(); showImage(imgIndex + 1); }}
                                        aria-label="Next"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 focus:opacity-100 w-9 h-9 flex items-center justify-center bg-[color:var(--maroon)]/70 text-[color:var(--ivory)] hover:bg-[color:var(--maroon)] transition-colors duration-300"
                                        data-testid="product-next"
                                    >
                                        <ChevronRight size={20} strokeWidth={1.5} />
                                    </button>
                                </>
                            )}
                            {slideCount > 1 && !videoActive && (
                                <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2">
                                    {Array.from({ length: slideCount }).map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => showImage(i)}
                                            aria-label={`Show photo ${i + 1}`}
                                            className={`h-1.5 rounded-full transition-all duration-300 ${i === imgIndex ? "w-6 bg-[color:var(--maroon)]" : "w-1.5 bg-[color:var(--border-gold)]"}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {slideCount > 1 && (
                            <div className="flex gap-3 mt-4 flex-wrap">
                                {gallery.map((src, i) => (
                                    <button
                                        key={src + i}
                                        onClick={() => showImage(i)}
                                        className={`w-16 h-20 overflow-hidden border transition-colors duration-300 ${i === imgIndex ? "border-[color:var(--maroon)]" : "border-[color:var(--border-gold)] hover:border-[color:var(--copper)]"}`}
                                        data-testid={`product-thumb-${i}`}
                                    >
                                        <img src={isVideoUrl(src) ? videoPoster(src) : cld(src, 160)} alt="" loading="lazy" decoding="async" draggable={false} className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Info */}
                    <div>
                        <p className="font-devanagari text-[color:var(--gold)] text-sm">{product.region} · {product.occasion}</p>
                        <h1 className="font-heading text-4xl lg:text-5xl text-[color:var(--maroon)] mt-2 leading-tight">{shownName}</h1>
                        <p className="font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--copper)] mt-3">{product.material}</p>

                        {product.review_count > 0 && (
                            <a href="#reviews" className="inline-flex items-center gap-1.5 mt-3 font-body text-sm text-[color:var(--ink)] hover:text-[color:var(--maroon)]">
                                <span className="flex items-center gap-0.5">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star key={n} size={14} className={n <= Math.round(product.avg_rating) ? "fill-[color:var(--gold)] text-[color:var(--gold)]" : "text-[color:var(--border-gold)]"} />
                                    ))}
                                </span>
                                {product.avg_rating} ({product.review_count} review{product.review_count === 1 ? "" : "s"})
                            </a>
                        )}

                        <div className="flex items-baseline gap-4 mt-6">
                            <span className="font-heading text-3xl text-[color:var(--gold)] font-semibold">{formatINR(product.price)}</span>
                            {product.original_price && (
                                <>
                                    <span className="font-body text-lg text-[color:var(--ink-muted)] line-through">{formatINR(product.original_price)}</span>
                                    <span className="text-xs font-ui tracking-widest-x uppercase text-[color:var(--emerald)]">
                                        Save {Math.round((1 - product.price / product.original_price) * 100)}%
                                    </span>
                                </>
                            )}
                        </div>

                        {tracked && (
                            <div className="mt-4">
                                {outOfStock ? (
                                    <span className="inline-flex items-center gap-1.5 font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--maroon)] bg-[color:var(--maroon)]/10 px-3 py-1.5">
                                        <PackageX size={13} /> Out of Stock
                                    </span>
                                ) : lowStock ? (
                                    <span className="inline-flex items-center gap-1.5 font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--copper)] bg-[color:var(--copper)]/10 px-3 py-1.5">
                                        <AlertTriangle size={13} /> Only {product.stock} left in stock
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--emerald)] bg-[color:var(--emerald)]/10 px-3 py-1.5">
                                        <PackageCheck size={13} /> In Stock
                                    </span>
                                )}
                            </div>
                        )}

                        <PaisleyDivider className="my-8" />

                        {/* Sizes */}
                        {product.sizes?.length > 0 && (
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--maroon)]">Size · in inches</p>
                                    <Link to="/size-guide" className="font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--copper)] inline-flex items-center gap-1.5 hover:text-[color:var(--maroon)]">
                                        <Ruler size={12} /> Find Your Size
                                    </Link>
                                </div>
                                <div className="flex gap-3 flex-wrap">
                                    {product.sizes.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => setSize(s)}
                                            className={`w-16 h-12 border font-body text-sm transition-colors duration-300 ${size === s ? "border-[color:var(--maroon)] bg-[color:var(--maroon)] text-[color:var(--ivory)]" : "border-[color:var(--border-gold)] text-[color:var(--maroon)] hover:border-[color:var(--maroon)]"}`}
                                            data-testid={`size-option-${s}`}
                                        >
                                            {s}"
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Colors */}
                        {product.colors?.length > 0 && (
                            <div className="mb-8">
                                <p className="font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--maroon)] mb-3">
                                    Colour · <span className="normal-case text-[color:var(--ink-muted)]">{color}</span>
                                </p>
                                <div className="flex gap-3 flex-wrap">
                                    {product.colors.map((c) => (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => { setColor(c); setImgIndex(0); }}
                                            title={c}
                                            aria-label={c}
                                            style={swatchStyle(c)}
                                            className={`w-10 h-10 rounded-full border-2 transition-all duration-300 ${color === c ? "border-[color:var(--maroon)] scale-110" : "border-[color:var(--border-gold)] hover:border-[color:var(--copper)]"}`}
                                            data-testid={`color-option-${c}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex gap-3 flex-wrap">
                            <button
                                onClick={handleBuyNow}
                                disabled={outOfStock}
                                className={`btn-gold flex-1 min-w-[220px] inline-flex items-center justify-center gap-2 ${outOfStock ? "opacity-50 cursor-not-allowed" : ""}`}
                                data-testid="buy-now-button"
                            >
                                <Zap size={14} /> {outOfStock ? "Out of Stock" : "Buy Now"}
                            </button>
                            <button
                                onClick={handleAdd}
                                disabled={outOfStock}
                                className={`btn-primary flex-1 min-w-[220px] ${outOfStock ? "opacity-50 cursor-not-allowed" : ""}`}
                                data-testid="add-to-cart-button"
                            >
                                <ShoppingBag size={14} /> {outOfStock ? "Out of Stock" : "Add to Cart"}
                            </button>
                            <button
                                onClick={() => toggleWishlist(product)}
                                className="btn-secondary"
                                data-testid="wishlist-button"
                            >
                                <Heart size={14} className={wishlisted ? "fill-[color:var(--maroon)]" : ""} />
                                {wishlisted ? "Wishlisted" : "Wishlist"}
                            </button>
                        </div>

                        {/* Trust */}
                        <div className="grid grid-cols-3 gap-4 mt-8 py-6 border-y border-[color:var(--border-gold)]">
                            {[
                                { icon: Truck, label: "Free Shipping" },
                                { icon: RotateCcw, label: "24 Hours Returns" },
                                { icon: Shield, label: "Authenticity Certified" },
                            ].map((f) => (
                                <div key={f.label} className="text-center">
                                    <f.icon size={18} className="mx-auto text-[color:var(--copper)]" strokeWidth={1.4} />
                                    <p className="font-ui text-[10px] tracking-widest-x uppercase text-[color:var(--ink-muted)] mt-2">{f.label}</p>
                                </div>
                            ))}
                        </div>

                        {/* Accordions */}
                        <div className="mt-8 space-y-1">
                            {accordions.map((a) => (
                                <div key={a.key} className="border-b border-[color:var(--border-gold)]">
                                    <button
                                        onClick={() => setOpenAccordion(openAccordion === a.key ? "" : a.key)}
                                        className="w-full py-4 flex items-center justify-between text-left"
                                        data-testid={`accordion-${a.key}`}
                                    >
                                        <span className="font-heading text-lg text-[color:var(--maroon)] flex items-center gap-2">
                                            <Paisley size={14} /> {a.label}
                                        </span>
                                        <ChevronDown size={16} className={`text-[color:var(--maroon)] transition-transform duration-300 ${openAccordion === a.key ? "rotate-180" : ""}`} />
                                    </button>
                                    {openAccordion === a.key && (
                                        <p className="pb-5 font-body text-[color:var(--ink)] leading-relaxed">{a.content}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Reviews */}
                <div id="reviews" className="mt-24 max-w-3xl mx-auto">
                    <div className="text-center mb-10">
                        <Lotus className="mx-auto mb-3" />
                        <h2 className="font-heading text-3xl md:text-4xl text-[color:var(--maroon)]">Ratings & Reviews</h2>
                        {product.review_count > 0 ? (
                            <p className="font-body text-sm text-[color:var(--ink-muted)] mt-2">
                                {product.avg_rating} out of 5 · {product.review_count} review{product.review_count === 1 ? "" : "s"}
                            </p>
                        ) : (
                            <p className="font-body text-sm text-[color:var(--ink-muted)] mt-2">Be the first to rate this piece</p>
                        )}
                    </div>

                    <div className="border border-[color:var(--border-gold)] bg-white p-6 md:p-8 mb-10">
                        {customer ? (
                            <>
                                <p className="font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--maroon)] mb-3">Rate This Piece</p>
                                <div className="flex gap-1.5 mb-4">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setReviewRating(n)}
                                            onMouseEnter={() => setReviewHoverRating(n)}
                                            onMouseLeave={() => setReviewHoverRating(0)}
                                            aria-label={`${n} star`}
                                        >
                                            <Star size={26} className={n <= (reviewHoverRating || reviewRating) ? "fill-[color:var(--gold)] text-[color:var(--gold)]" : "text-[color:var(--border-gold)]"} />
                                        </button>
                                    ))}
                                </div>
                                <textarea
                                    value={reviewComment}
                                    onChange={(e) => setReviewComment(e.target.value)}
                                    placeholder="How was your experience with this piece?"
                                    rows={3}
                                    className="w-full border border-[color:var(--border-gold)] p-3 font-body text-sm text-[color:var(--ink)] focus:outline-none focus:border-[color:var(--maroon)] transition-colors duration-300"
                                />
                                <div className="flex items-center gap-3 flex-wrap mt-3">
                                    {reviewImages.map((src, i) => (
                                        <div key={src + i} className="relative w-16">
                            {!isVideoUrl(src) && onMakeMain && (
                                <button type="button" onClick={() => onMakeMain(src)} className="block w-full mb-1 text-[9px] font-ui uppercase tracking-wider text-[color:var(--maroon)] border border-[color:var(--border-gold)] hover:bg-[color:var(--ivory-2)]" title="Make this the main (first) photo">Make main</button>
                            )}
                                            <img src={src} alt="" className="w-16 h-16 object-cover border border-[color:var(--border-gold)]" />
                                            <button
                                                type="button"
                                                onClick={() => setReviewImages((prev) => prev.filter((_, j) => j !== i))}
                                                className="absolute -top-2 -right-2 bg-[color:var(--maroon)] text-white w-5 h-5 rounded-full text-xs leading-none"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                    {reviewImages.length < 3 && (
                                        <label className="w-16 h-16 border border-dashed border-[color:var(--border-gold)] flex flex-col items-center justify-center gap-1 cursor-pointer text-[color:var(--copper)] hover:border-[color:var(--maroon)] transition-colors duration-300">
                                            <Camera size={16} />
                                            <span className="text-[9px] font-ui uppercase">{reviewUploading ? "…" : "Add"}</span>
                                            <input type="file" accept="image/*" multiple className="hidden" onChange={handleReviewImageSelect} disabled={reviewUploading} />
                                        </label>
                                    )}
                                </div>
                                <button
                                    onClick={handleSubmitReview}
                                    disabled={reviewSubmitting}
                                    className="btn-primary mt-5 disabled:opacity-60"
                                >
                                    {reviewSubmitting ? "Submitting…" : "Submit Review"}
                                </button>
                            </>
                        ) : (
                            <p className="font-body text-sm text-[color:var(--ink-muted)] text-center">
                                <Link to="/login" className="text-[color:var(--maroon)] underline">Log in</Link> to rate this piece and share photos.
                            </p>
                        )}
                    </div>

                    {reviews.length > 0 && (
                        <div className="space-y-6">
                            {reviews.map((r) => (
                                <div key={r.id} className="border-b border-[color:var(--border-gold)] pb-6">
                                    <div className="flex items-center gap-1.5">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star key={n} size={13} className={n <= r.rating ? "fill-[color:var(--gold)] text-[color:var(--gold)]" : "text-[color:var(--border-gold)]"} />
                                        ))}
                                        {r.verified_purchase && (
                                            <span className="ml-2 font-ui text-[10px] tracking-widest-x uppercase text-[color:var(--emerald)]">Verified Purchase</span>
                                        )}
                                    </div>
                                    <p className="font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--maroon)] mt-2">{r.customer_name}</p>
                                    {r.comment && <p className="font-body text-sm text-[color:var(--ink)] mt-2 leading-relaxed">{r.comment}</p>}
                                    {r.images?.length > 0 && (
                                        <div className="flex gap-3 mt-3 flex-wrap">
                                            {r.images.map((src, i) => (
                                                <img key={src + i} src={src} alt="" className="w-16 h-16 object-cover border border-[color:var(--border-gold)]" />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Related */}
                {related.length > 0 && (
                    <div className="mt-24">
                        <div className="text-center mb-10">
                            <Lotus className="mx-auto mb-3" />
                            <h2 className="font-heading text-3xl md:text-4xl text-[color:var(--maroon)]">You may also love</h2>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                        </div>
                    </div>
                )}
            </div>

            {/* Full-screen photo viewer */}
            {lightboxOpen && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center"
                    style={{ backgroundColor: "rgba(42, 22, 26, 0.92)" }}
                    onClick={() => setLightboxOpen(false)}
                    data-testid="product-image-lightbox"
                >
                    <button
                        onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
                        aria-label="Close"
                        className="absolute top-6 right-6 text-[color:var(--ivory)] hover:text-[color:var(--gold)] transition-colors duration-300"
                    >
                        <X size={28} strokeWidth={1.5} />
                    </button>

                    {gallery.length > 1 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); showPhoto(imgIndex - 1); }}
                            aria-label="Previous photo"
                            className="absolute left-4 md:left-8 text-[color:var(--ivory)] hover:text-[color:var(--gold)] transition-colors duration-300"
                        >
                            <ChevronLeft size={36} strokeWidth={1.5} />
                        </button>
                    )}

                    {isVideoUrl(gallery[imgIndex]) ? (
                        <video
                            key={gallery[imgIndex]}
                            src={optImg(gallery[imgIndex], 1600)}
                            loop
                            autoPlay
                            muted
                            playsInline
                            onClick={(e) => e.stopPropagation()}
                            className="max-w-[92vw] max-h-[86vh] bg-black"
                        />
                    ) : (
                        <img
                            src={optImg(gallery[imgIndex], 1600)}
                            alt={`${product.name} — full view`}
                            onClick={(e) => e.stopPropagation()}
                            className="max-w-[92vw] max-h-[86vh] object-contain select-none"
                            draggable={false}
                        />
                    )}

                    {gallery.length > 1 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); showPhoto(imgIndex + 1); }}
                            aria-label="Next photo"
                            className="absolute right-4 md:right-8 text-[color:var(--ivory)] hover:text-[color:var(--gold)] transition-colors duration-300"
                        >
                            <ChevronRight size={36} strokeWidth={1.5} />
                        </button>
                    )}

                    {gallery.length > 1 && (
                        <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-2">
                            {gallery.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={(e) => { e.stopPropagation(); showPhoto(i); }}
                                    aria-label={`Show photo ${i + 1}`}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${i === imgIndex ? "w-6 bg-[color:var(--gold)]" : "w-1.5 bg-[color:var(--ivory)]/40"}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProductDetail;
